/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, ButtonGroup, colors, HeadingSection, HFlow, Text, Theme } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { confirm } from 'components/modals/confirm'
import theme from 'config/theme'
import puericulturaImage from 'images/soap/ic_puericultura.png'
import { Fragment, useMemo } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { grupoCboPuericultura } from '../acessos'
import PuericulturaView, { PuericulturaModel } from '../puericultura/PuericulturaView'
import { idadeMaiorQue } from '../puericultura/util/idadeMaiorQue'

enum FormAtivoEnum {
  PUERICULTURA,
  PRE_NATAL,
}

export interface SwitchButtonObjetivoFormModel {
  formAtivo: FormAtivoEnum
  puericultura: PuericulturaModel
}

interface SwitchButtonObjetivoFormProps {
  name: MetaPath<SwitchButtonObjetivoFormModel>
  cidadaoDataNascimento: LocalDate
  prontuarioId: ID
}

const LIMITE_IDADE_PUERICULTURA = 21

export function SwitchButtonObjetivoForm(props: SwitchButtonObjetivoFormProps) {
  const { name, cidadaoDataNascimento, prontuarioId } = props
  const acessoIdadePuericultura = !idadeMaiorQue(cidadaoDataNascimento, LIMITE_IDADE_PUERICULTURA)

  const {
    input: {
      value: { formAtivo },
      onChange: updateForm,
    },
  } = useField<SwitchButtonObjetivoFormModel>(name.absolutePath())

  const setFormAtivo = (stateButton: FormAtivoEnum) => updateForm({ formAtivo: stateButton })

  const classes = useMemo(() => styles(theme, formAtivo), [formAtivo])

  const handleClickPuericultura = (stateButton: FormAtivoEnum) => {
    // TODO foundation - mostrar msg de confirmação somente com form sujo
    confirm({
      title: 'Deseja descartar os dados de Puericultura?',
      body: 'As alterações realizadas serão perdidas.',
      cancelLabel: 'Não, continuar aqui',
      confirmLabel: 'Sim, cancelar',
      onConfirm: () => {
        setFormAtivo(formAtivo === stateButton ? undefined : stateButton)
      },
    })()
  }

  const handleClick = (stateButton: FormAtivoEnum) => () => {
    switch (formAtivo) {
      case FormAtivoEnum.PUERICULTURA:
        return handleClickPuericultura(stateButton)
      default:
        return setFormAtivo(stateButton)
    }
  }

  return (
    // TO DO - Checar se o checkAcess é pertitente para todos os forms do Fragment
    <CheckPermission permission={grupoCboPuericultura}>
      <Fragment>
        <HeadingSection level={4} title='Registrar informações de' />
        <ButtonGroup>
          <HFlow hSpacing={1} justifyContent='flex-start'>
            {acessoIdadePuericultura && (
              <Button
                size='medium'
                style={classes.puericulturaButton}
                onClick={handleClick(FormAtivoEnum.PUERICULTURA)}
              >
                <HFlow alignItems='center'>
                  <img src={puericulturaImage} alt='Puericultura' />
                  <Text fontWeight='bold' style={classes.puericulturaButtonText}>
                    Puericultura
                  </Text>
                </HFlow>
              </Button>
            )}
          </HFlow>
        </ButtonGroup>

        {formAtivo === FormAtivoEnum.PUERICULTURA && acessoIdadePuericultura && (
          <PuericulturaView
            name={name.puericultura}
            cidadaoDataNascimento={cidadaoDataNascimento}
            prontuarioId={prontuarioId}
          />
        )}
      </Fragment>
    </CheckPermission>
  )
}

const styles = (theme: Theme, formAtivo: FormAtivoEnum) => ({
  puericulturaButton: css`
    border: 1px ${formAtivo === FormAtivoEnum.PUERICULTURA ? colors.turquoise.c50 : colors.turquoise.c60} solid;
    background-color: ${formAtivo === FormAtivoEnum.PUERICULTURA ? colors.turquoise.c60 : theme.pallete.gray.c100};
    margin: 0;
    &:not(:disabled):hover {
      background-color: ${formAtivo === FormAtivoEnum.PUERICULTURA ? colors.turquoise.c50 : colors.turquoise.c90};
    }
  `,
  puericulturaButtonText: css`
    color: ${formAtivo === FormAtivoEnum.PUERICULTURA ? theme.pallete.gray.c100 : colors.turquoise.c40};
  `,
})
